import type { HumanConversationNotifications } from './direct-call-events';

interface Listener {
  changed: (conversationId: string) => void;
  reconnected: () => void;
  failed: () => void;
}

/**
 * WI 33903 AC3: one human-session hub shared by every open conversation (the
 * visible call and the background thread feeds). The bridge sends
 * `newMessage`, frames and RTC to the human's user id with the conversation
 * id in the payload, so one hub already carries every conversation; each
 * {@link lease} filters by id as before. A lease's close releases only its own
 * listener, and the hub closes when the last listener goes, so opening or
 * closing a feed never cuts the visible call (code-pavel review on 19202891).
 * A failed hub is told to every listener and dropped; the next subscribe
 * starts a fresh one. Speech stays on the one live hub's binding.
 */
export class SharedHumanCallNotifications<
  Hub extends HumanConversationNotifications,
> {
  private hub: Hub | undefined;
  private stopHub: (() => void) | undefined;
  private readonly listeners = new Set<Listener>();

  constructor(
    private readonly create: () => Hub,
    private readonly created: (hub: Hub) => void = () => undefined
  ) {}

  /** The live hub, for speech, frames and RTC; undefined when none is open. */
  get current(): Hub | undefined {
    return this.hub;
  }

  lease(): HumanConversationNotifications {
    let listener: Listener | undefined;
    const release = () => {
      if (!listener) return;
      this.listeners.delete(listener);
      listener = undefined;
      if (this.listeners.size === 0) this.drop();
    };
    return {
      subscribe: (changed, reconnected, failed) => {
        if (listener) throw new Error('Notifications are unavailable');
        listener = { changed, reconnected, failed };
        this.listeners.add(listener);
        this.open();
        return release;
      },
      close: release,
    };
  }

  private open(): void {
    if (this.hub) return;
    const hub = this.create();
    this.hub = hub;
    this.created(hub);
    this.stopHub = hub.subscribe(
      (id) => {
        if (this.hub === hub)
          for (const l of [...this.listeners]) l.changed(id);
      },
      () => {
        if (this.hub === hub)
          for (const l of [...this.listeners]) l.reconnected();
      },
      () => {
        if (this.hub !== hub) return;
        this.drop();
        for (const l of [...this.listeners]) l.failed();
      }
    );
  }

  private drop(): void {
    const hub = this.hub;
    const stop = this.stopHub;
    this.hub = undefined;
    this.stopHub = undefined;
    try {
      stop?.();
    } finally {
      hub?.close();
    }
  }
}
